import { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useLocation, useNavigate } from 'react-router-dom';
import { FiAlertTriangle, FiCheckCircle, FiDownload } from 'react-icons/fi';
import toast from 'react-hot-toast';
import { QUERY_KEY } from '../../../../../services/queryEndPoint';
import { getAuthToken, setAuthToken, decodeAuthTokenPayload, buildLoginRedirectUrl, refreshAuthSession, removeAuthToken } from '../../../../../helper/helper';
import { useAppSelector } from '../../../../../redux/hooks';
import { selectSelectedLeague } from '../../../../../redux/slice/selectedLeagueSlice';
import { canManageWaiversInLeague } from '../../../../../utils/leagueAccess';
import { fetchWaiver } from '../../api/waiverApi';
import { fetchMyWaiverStatus, fetchWaiverSignatureStatus } from './waiverSignatureApi';
import { exportSignedWaiverToPDF } from './waiverPdfExport';
import CompleteWaiverWizard from './CompleteWaiverWizard';
import AdminWaiverPanel from './AdminWaiverPanel';
import './MyWaiverTab.scss';

interface Props {
  tournamentId: string | null;
  tournamentName?: string | null;
  leagueId: string | null;
}

function formatPlayerRole(role: string | null): string {
  if (!role) return '-';
  return role
    .toLowerCase()
    .split('_')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join('-');
}

export default function MyWaiverTab({ tournamentId, tournamentName, leagueId }: Props) {
  const [showWizard, setShowWizard] = useState(false);
  const [isDownloadingPdf, setIsDownloadingPdf] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  // Bridges a token the mobile app's in-app WebView appended to this URL (see
  // ShareLinkGenerator.buildWaiverUrl) into the site's own auth cookie, so a
  // signed-in app user isn't asked to log in again just to view this tab.
  // Deliberately synchronous (not a useEffect) so it runs before getAuthToken()
  // below on this same, first render — an effect would fire one render too
  // late. Only bridges when no session already exists, so it can never
  // clobber a real signed-in browser session with a stale/foreign token.
  const mobileToken = new URLSearchParams(location.search).get('mobileToken');
  if (mobileToken && !getAuthToken()) {
    setAuthToken(mobileToken);
  }

  // Strips the token from the URL once it's been bridged, so it doesn't linger
  // in the WebView's own history/back-stack.
  useEffect(() => {
    if (!mobileToken) return;
    const params = new URLSearchParams(location.search);
    params.delete('mobileToken');
    navigate({ pathname: location.pathname, search: params.toString() }, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mobileToken]);

  const token = getAuthToken();
  const myPlayerId = token ? (decodeAuthTokenPayload(token)?.sub as string | undefined) : undefined;
  const isSignedIn = !!myPlayerId;

  const { data: status, isLoading, isError, error, refetch } = useQuery({
    queryKey: [QUERY_KEY.tournamentWaiverSignature, tournamentId, 'my-waiver-status'],
    queryFn: () => fetchMyWaiverStatus(tournamentId as string),
    enabled: !!tournamentId && isSignedIn,
    // A 403 here always means "not really authenticated" (self-only endpoint, no other 403
    // reason exists) — retrying won't fix that, so fail fast into the redirect below instead of
    // burning a few seconds on React Query's default retries first.
    retry: false,
  });

  // Two distinct "not really logged in" cases land here, both sent to the same place: (1) no
  // token at all (never logged in, or logged out) — checked before the query even runs; (2) a
  // token that decodes fine client-side but the backend rejects as anonymous (expired/invalid —
  // JwtAuthenticationFilter doesn't fail the request outright, it just leaves the caller
  // unauthenticated, so this surfaces as a 403 here, not a 401 the global interceptor would
  // already catch). Sending the player through /login and back is what makes a waiver reminder
  // email link actually work when the session has lapsed since it was sent.
  // Always carry ?tab=waiver, so signing in (or back in) lands on this tab rather than the tournament's default one.
  const returnParams = new URLSearchParams(location.search);
  returnParams.set('tab', 'waiver');
  returnParams.delete('mobileToken');
  const returnPath = `${location.pathname}?${returnParams.toString()}`;
  const sessionInvalid = isError && (error as { response?: { status?: number } })?.response?.status === 403;
  const sessionHandled = useRef(false);
  useEffect(() => {
    if (sessionHandled.current) return;
    if (!isSignedIn) {
      sessionHandled.current = true;
      navigate(buildLoginRedirectUrl(returnPath), { replace: true });
      return;
    }
    if (!sessionInvalid) return;
    sessionHandled.current = true;
    void (async () => {
      // A 403 here means the backend saw an anonymous caller, i.e. an expired access token. The global
      // interceptor only refreshes on 401, so try the refresh token once and retry before giving up.
      const fresh = await refreshAuthSession();
      if (fresh) {
        const retried = await refetch();
        if (!retried.isError) {
          sessionHandled.current = false;
          return;
        }
      }
      // Clear the dead token first: /login forwards anyone who still has a token straight back to the
      // redirect target, which would bounce the user back here (and on to the default tab).
      removeAuthToken();
      navigate(buildLoginRedirectUrl(returnPath), { replace: true });
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSignedIn, sessionInvalid]);

  const isOnTeam = !!status?.teamSnapshotId;

  const selectedLeague = useAppSelector(selectSelectedLeague);
  const isAdmin = canManageWaiversInLeague(selectedLeague);

  const bannerState = useMemo<'pending' | 'completed' | null>(() => {
    if (!status || !status.waiverRequired) return null;
    return status.signed ? 'completed' : 'pending';
  }, [status]);

  // Fetched on demand rather than eagerly — most visits to this tab don't need the PDF, and
  // this needs two extra requests (the signature itself, plus the waiver's agreement content/
  // configured fields, neither of which my-waiver-status carries).
  const handleDownloadPdf = async () => {
    if (!tournamentId || !leagueId || !myPlayerId || !status?.waiverId || isDownloadingPdf) return;
    setIsDownloadingPdf(true);
    try {
      const [sigStatus, waiver] = await Promise.all([
        fetchWaiverSignatureStatus(tournamentId, myPlayerId),
        fetchWaiver(leagueId, status.waiverId),
      ]);
      const signature = sigStatus.signature;
      if (!signature) {
        toast.error('Could not find your signed waiver.');
        return;
      }
      await exportSignedWaiverToPDF({
        waiverName: waiver.name,
        tournamentName: tournamentName ?? '-',
        teamName: status.teamName ?? '-',
        signedBy: signature.digitalSignature ?? '-',
        signedOn: signature.signedAt ? new Date(signature.signedAt).toLocaleDateString() : '-',
        agreementContent: waiver.agreementContent,
        signatureType: signature.signatureType,
        signatureImageUrl: signature.digitalSignatureUrl,
        agreeCheckboxLabel: waiver.agreeCheckboxLabel,
        agreedToTerms: signature.agreedToTerms,
        signerIsMinor: !!signature.parentGuardianName,
        parentGuardianName: signature.parentGuardianName,
        playerInfoFields: waiver.playerInfoFields,
        customFields: waiver.customFields,
        fieldValues: signature.fieldValues,
      });
    } catch {
      toast.error('Could not download your signed waiver. Please try again.');
    } finally {
      setIsDownloadingPdf(false);
    }
  };

  if (!isSignedIn || sessionInvalid) {
    // The effect above is already navigating away — this only renders for the one frame before
    // that completes.
    return (
      <div className="my-waiver-tab">
        <p className="my-waiver-empty">{sessionInvalid ? 'Checking your session…' : 'Redirecting to login…'}</p>
      </div>
    );
  }

  if (isLoading || !tournamentId) {
    return (
      <div className="my-waiver-tab">
        <p className="my-waiver-empty">Loading…</p>
      </div>
    );
  }

  if (showWizard && status) {
    return (
      <CompleteWaiverWizard
        tournamentId={tournamentId}
        tournamentName={tournamentName ?? null}
        leagueId={leagueId}
        waiverId={status.waiverId}
        teamName={status.teamName}
        onClose={() => setShowWizard(false)}
        onSigned={() => {
          setShowWizard(false);
          void refetch();
        }}
      />
    );
  }

  if (!status || !status.waiverRequired) {
    return (
      <div className="my-waiver-tab">
        <p className="my-waiver-empty">This tournament does not require a waiver.</p>
      </div>
    );
  }

  return (
    <div className="my-waiver-tab">
      {bannerState && (
        <div className={`my-waiver-banner my-waiver-banner--${bannerState}`}>
          <div className="my-waiver-banner-text">
            <span className="my-waiver-banner-icon" aria-hidden>
              {bannerState === 'completed' ? <FiCheckCircle size={20} /> : <FiAlertTriangle size={20} />}
            </span>
            <div>
              <p className="my-waiver-banner-title">Waiver required before you can be selected</p>
              <p className="my-waiver-banner-subtitle">
                {!isOnTeam
                  ? `You'll be able to complete this once you're added to a team for ${tournamentName ?? 'this tournament'}.`
                  : status.teamName
                    ? `You've been added to ${status.teamName} for ${tournamentName ?? 'this tournament'}. Takes about a minute.`
                    : `Complete your waiver for ${tournamentName ?? 'this tournament'}. Takes about a minute.`}
              </p>
            </div>
          </div>
          {bannerState === 'completed' ? (
            <div className="my-waiver-banner-actions">
              <span className="my-waiver-banner-badge">Completed</span>
              <button
                type="button"
                className="my-waiver-btn my-waiver-btn--secondary"
                onClick={handleDownloadPdf}
                disabled={isDownloadingPdf}
              >
                <FiDownload size={16} aria-hidden />
                {isDownloadingPdf ? 'Preparing…' : 'Download PDF'}
              </button>
            </div>
          ) : (
            // Only players actually rostered to a team for this tournament can sign — signing
            // requires a team context, and there's nothing to select them for otherwise.
            isOnTeam && (
              <button type="button" className="my-waiver-btn my-waiver-btn--primary" onClick={() => setShowWizard(true)}>
                Complete Waiver
              </button>
            )
          )}
        </div>
      )}

      {status.teamName && (
        <div className="my-waiver-roster-card">
          <div className="my-waiver-roster-header">
            <div>
              <h2>{status.teamName}</h2>
              <p>{tournamentName ?? '-'}</p>
            </div>
            <span className={`my-waiver-status-pill my-waiver-status-pill--${bannerState}`}>
              {bannerState === 'completed' ? 'Waiver completed' : 'Waiver pending'}
            </span>
          </div>

          <table className="my-waiver-roster-table">
            <thead>
              <tr>
                <th>Player</th>
                <th>Role</th>
                <th>Waiver</th>
              </tr>
            </thead>
            <tbody>
              {status.teammates.map((teammate) => (
                <tr key={teammate.playerId} className={teammate.self ? 'is-self' : ''}>
                  <td>{teammate.playerName || '-'}</td>
                  <td>{formatPlayerRole(teammate.playerRole)}</td>
                  <td>
                    <span className={`my-waiver-status-pill my-waiver-status-pill--${teammate.signed ? 'completed' : 'pending'}`}>
                      {teammate.signed ? 'Signed' : 'Pending'}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {isAdmin && leagueId && (
        <AdminWaiverPanel tournamentId={tournamentId} leagueId={leagueId} tournamentName={tournamentName} />
      )}
    </div>
  );
}
